import type { RetailWalletChallengeResponse } from "@solomon-apps/shared";

import { isRecord, isWalletChallenge, readResponseJson } from "./verificationResponse";

/** Request the challenge used by account and position wallet actions. */
export async function requestWalletActionChallenge(
    url: string,
    payload: object,
    fallbackMessage: string
): Promise<RetailWalletChallengeResponse> {
    const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
    });
    if (!response.ok) {
        const body = await readResponseJson(response);
        const error = isRecord(body) ? body.error : null;
        const message =
            typeof error === "string"
                ? error
                : isRecord(error) && typeof error.message === "string"
                  ? error.message
                  : isRecord(body) && typeof body.message === "string"
                    ? body.message
                    : fallbackMessage;
        throw new Error(message);
    }
    const body = await readResponseJson(response);
    if (!isWalletChallenge(body)) throw new Error("The wallet verification challenge was invalid");
    return body;
}

/** A confirmed transfer can reach the wallet RPC before the API's RPC sees
 * it. Retry only the HTTP proof submission; never build or broadcast again. */
export async function submitWithVerificationRetry(
    url: string,
    init: RequestInit,
    challengeExpiresAt: string
): Promise<Response> {
    const stopAt = Math.min(Date.parse(challengeExpiresAt), Date.now() + 60_000);
    for (;;) {
        init.signal?.throwIfAborted();
        const response = await fetch(url, init);
        if (response.status !== 409) return response;
        const body = await readResponseJson(response.clone());
        const error = isRecord(body) ? body.error : null;
        if (
            !isRecord(body) ||
            (body.code !== "verification_pending" && (!isRecord(error) || error.code !== "verification_pending"))
        ) {
            return response;
        }
        if (!Number.isFinite(stopAt) || Date.now() + 1_500 >= stopAt) return response;
        await new Promise<void>((resolve) => setTimeout(resolve, 1_500));
    }
}
